import { useEffect } from 'react';
import { refreshSession } from '../helpers/api';
import { getLoggedinUser } from '../helpers/api_helper';

// Refresh ~10 min before the 1-hour access token (application.properties:
// access-expire-length) can expire. The JWT lives in an HttpOnly cookie so the client
// can't read its exp — a fixed sub-TTL interval is the standard approach.
const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
const LAST_REFRESH_KEY = 'arcmedix.lastProactiveRefresh';

const lastRefreshAt = () => {
  const ts = parseInt(localStorage.getItem(LAST_REFRESH_KEY) || '0', 10);
  return Number.isFinite(ts) ? ts : 0;
};

/**
 * Keeps an authenticated session alive by proactively refreshing the access token
 * before it can expire under an active user — so long form fills (and any idle but
 * open tab) never hit the backend's "Unauthorized access, please log in first." 401.
 *
 * - Runs only while logged in (getLoggedinUser()).
 * - Coordinates across tabs via a localStorage timestamp so N open tabs don't all
 *   rotate the refresh token in lockstep (which would brush the 120s rotation grace).
 *   The slot is claimed before the call so a sibling tab firing at the same instant
 *   backs off — this shrinks the rotation race rather than feeding it.
 * - Also refreshes on tab wake/focus and re-checks every minute, covering laptop sleep
 *   and background-tab timer throttling where a single 50-min timer would drift.
 * - Never forces logout on failure: a genuinely dead session is handled by the next
 *   real call's reactive interceptor (helpers/api.js) + the boot /auth/me reconcile.
 */
export default function useSessionKeepAlive() {
  useEffect(() => {
    let cancelled = false;

    const maybeRefresh = async () => {
      if (cancelled || !getLoggedinUser()) return;
      // One refresh per interval across all tabs and all trigger events.
      if (Date.now() - lastRefreshAt() < REFRESH_INTERVAL_MS) return;
      const claimedAt = Date.now();
      localStorage.setItem(LAST_REFRESH_KEY, String(claimedAt));
      try {
        await refreshSession();
      } catch {
        // Roll back our claim so the next tick retries (≥60s apart); the reactive
        // interceptor still recovers any real call in the meantime.
        if (lastRefreshAt() === claimedAt) localStorage.removeItem(LAST_REFRESH_KEY);
      }
    };

    maybeRefresh(); // establish a known-fresh token on mount (deduped across tabs)
    const interval = setInterval(maybeRefresh, 60 * 1000); // re-check each minute; gated by the interval
    const onWake = () => { if (document.visibilityState === 'visible') maybeRefresh(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);
}
